import { AfterViewInit, Directive, ElementRef, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { v4 as uuid } from 'uuid';

import { CorePlatformService } from '@core/infrastructure/platform/platform.service';

interface InViewportExtraOptions {
    minVisibleTime?: number;
}

const InViewportExtraOptionsInitialState = {
    minVisibleTime: 0,
};

export const INVIEWPORT_ID_ATTRIBUTE_NAME = 'inviewport-element-id';

@Directive({
    selector: '[inViewport]',
    standalone: true,
})
export class InViewportDirective implements AfterViewInit, OnDestroy {
    @Input() public inViewportExtraOptions: InViewportExtraOptions = InViewportExtraOptionsInitialState;

    @Input() public readonly inViewportThreshold: number[] = [0];

    @Output() public readonly inViewport = new EventEmitter<IntersectionObserverEntry>();

    @Output() public readonly outViewPort = new EventEmitter<IntersectionObserverEntry>();

    private intersectionObserver: IntersectionObserver;

    private timerId = null;

    constructor(
        private readonly elementRef: ElementRef,
        private readonly platformService: CorePlatformService,
    ) { }

    public async ngAfterViewInit() {
        if (!this.platformService.isBrowser()) {
            return;
        }

        const element = await this.elementRef.nativeElement;
        const options = {
            root: null,
            rootMargin: '100px',
            threshold: this.inViewportThreshold,
        };
        this.assignElementId(element);

        this.intersectionObserver = new IntersectionObserver((entries) => {
            // biome-ignore lint/complexity/noForEach: <explanation>
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    this.timerId = setTimeout(() => {
                        this.inViewport.emit(entry);
                    }, this.inViewportExtraOptions.minVisibleTime);
                } else {
                    clearTimeout(this.timerId);
                    this.outViewPort.emit(entry);
                }
            });
        }, options);

        this.intersectionObserver.observe(element);
    }

    public assignElementId(element: Element) {
        element.setAttribute(INVIEWPORT_ID_ATTRIBUTE_NAME, uuid());
    }

    public ngOnDestroy() {
        if (this.intersectionObserver) {
            this.intersectionObserver.unobserve(this.elementRef.nativeElement);
            clearTimeout(this.timerId);
        }
    }
}
